import React from 'react';

import useVisitStore from '../../stores/visitStore';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import { CancelConfirmDialog } from '../../components/dashboard/WorkflowShared';

/**
 * VisitCancelWidget — cancels a visit with a required comment, matching
 * the legacy CancelConfirmDialog UX. Wraps the existing dialog so the
 * shape is byte-identical to the pre-MVP flow; routing through the SDK
 * is the architectural win.
 *
 * Context:
 *   visitId — required
 *   prefill?.name — display name
 *   prefill?.userRole — passed through to the workflow log for the
 *     `MARK_CANCELLED` audit entry (the legacy ManageVisitPage path
 *     wrote this with `userRole` from PermissionContext).
 *
 * Side channel: dispatches `arogya:widgets:visit-cancelled` after a
 * successful cancel so list views can refresh.
 */
export default function VisitCancelWidget({ context, onClose }) {
  const updateVisit = useVisitStore((s) => s.updateVisit);
  const logWorkflow = useWorkflowStore((s) => s.logWorkflow);
  const visitId = context?.visitId;
  const name = (context?.prefill && context.prefill.name) || 'this patient';
  const userRole = (context?.prefill && context.prefill.userRole) || null;

  const isDark = typeof document !== 'undefined'
    && document.documentElement.classList.contains('dark');

  const handleConfirm = async (comment) => {
    if (!visitId) { onClose?.(); return; }
    try {
      await updateVisit(visitId, {
        status: 'CANCELLED',
        cancellation_reason: comment,
        cancelled_by_role: userRole,
      });
      try { logWorkflow?.(visitId, WORKFLOW_ACTIONS.MARK_CANCELLED); }
      catch (_e) { /* workflow log failure must not block UX */ }
      try {
        window.dispatchEvent(new CustomEvent('arogya:widgets:visit-cancelled', {
          detail: { visitId, comment },
        }));
      } catch (_e) { /* ignore */ }
    } finally {
      onClose?.();
    }
  };

  return (
    <CancelConfirmDialog
      isDark={isDark}
      name={name}
      onConfirm={handleConfirm}
      onCancel={() => onClose?.()}
    />
  );
}
